/**
 * Client-side merge of overlapping map clusters.
 *
 * INTERIM: the backend (search service) clusters with an Elasticsearch geohash grid whose
 * precision is derived from the bounding-box size, not from the zoom level, so neighbouring
 * cells often produce donuts that overlap on screen. Until the backend clusters by pixel
 * radius per zoom (see docs/map-clustering-backend-handoff.md) the widget merges clusters
 * whose markers would overlap. Remove this module once the backend delivers.
 *
 * Pure: works on already projected screen points, no Leaflet import.
 */

export interface ClusterLatLng {
  lat: number;
  lng: number;
}

export interface ClusterStatusCount {
  status: string;
  count: number;
}

export interface ClusterBoundingBox {
  northeast: ClusterLatLng;
  southwest: ClusterLatLng;
}

export interface ClusterData {
  center: ClusterLatLng;
  totalCount: number;
  singleLocation: boolean;
  assetStatusCounts: ClusterStatusCount[];
  boundingBox: ClusterBoundingBox;
}

export interface ProjectedItem<T> {
  /** Screen / layer point of the marker center at the current zoom. */
  x: number;
  y: number;
  /** Marker radius in px at that zoom. */
  radius: number;
  /** Ordering weight: bigger items become merge seeds first. */
  count: number;
  item: T;
}

/** Above this zoom nothing is merged so single locations stay individually clickable. */
export const MERGE_MAX_ZOOM = 17;

/** Fraction of the summed radii below which two markers count as overlapping. */
export const DEFAULT_OVERLAP_FACTOR = 0.75;

const SAME_POINT_EPSILON = 1e-6;

/**
 * Greedy grouping, largest item first: each unassigned seed absorbs every unassigned item
 * whose center is closer than `(r_seed + r_item) * overlapFactor` to the seed. Distances
 * are measured to the seed (not to a moving centroid) so the result is deterministic.
 * O(n²), fine for the few hundred clusters a viewport can hold.
 */
export const groupOverlapping = <T>(
  items: ProjectedItem<T>[],
  overlapFactor: number = DEFAULT_OVERLAP_FACTOR,
): T[][] => {
  const order = items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => b.item.count - a.item.count || a.index - b.index);
  const assigned = new Array<boolean>(items.length).fill(false);
  const groups: T[][] = [];

  for (const { item: seed, index: seedIndex } of order) {
    if (assigned[seedIndex]) continue;
    assigned[seedIndex] = true;
    const group: T[] = [seed.item];

    for (const { item: candidate, index } of order) {
      if (assigned[index]) continue;
      const threshold = (seed.radius + candidate.radius) * overlapFactor;
      const distance = Math.hypot(candidate.x - seed.x, candidate.y - seed.y);
      if (distance < threshold) {
        assigned[index] = true;
        group.push(candidate.item);
      }
    }
    groups.push(group);
  }

  return groups;
};

const samePoint = (a: ClusterLatLng, b: ClusterLatLng): boolean =>
  Math.abs(a.lat - b.lat) < SAME_POINT_EPSILON && Math.abs(a.lng - b.lng) < SAME_POINT_EPSILON;

/** Combine a group of clusters into one: summed counts, union bbox, count-weighted center. */
export const mergeClusterGroup = (members: ClusterData[]): ClusterData => {
  if (members.length === 1) return members[0];

  const totalCount = members.reduce((sum, m) => sum + m.totalCount, 0);
  const weightOf = (m: ClusterData) => (totalCount > 0 ? m.totalCount : 1);
  const totalWeight = totalCount > 0 ? totalCount : members.length;

  const statusCounts = new Map<string, number>();
  for (const member of members) {
    for (const { status, count } of member.assetStatusCounts) {
      statusCounts.set(status, (statusCounts.get(status) ?? 0) + count);
    }
  }

  const center = members.reduce(
    (acc, m) => ({
      lat: acc.lat + (m.center.lat * weightOf(m)) / totalWeight,
      lng: acc.lng + (m.center.lng * weightOf(m)) / totalWeight,
    }),
    { lat: 0, lng: 0 },
  );

  const boundingBox = members.reduce<ClusterBoundingBox>(
    (box, m) => ({
      southwest: {
        lat: Math.min(box.southwest.lat, m.boundingBox.southwest.lat),
        lng: Math.min(box.southwest.lng, m.boundingBox.southwest.lng),
      },
      northeast: {
        lat: Math.max(box.northeast.lat, m.boundingBox.northeast.lat),
        lng: Math.max(box.northeast.lng, m.boundingBox.northeast.lng),
      },
    }),
    {
      southwest: { ...members[0].boundingBox.southwest },
      northeast: { ...members[0].boundingBox.northeast },
    },
  );

  const singleLocation = members.every(
    (m) => m.singleLocation && samePoint(m.center, members[0].center),
  );

  return {
    center,
    totalCount,
    singleLocation,
    assetStatusCounts: Array.from(statusCounts, ([status, count]) => ({ status, count })),
    boundingBox,
  };
};

/** Group projected clusters by marker overlap and merge each group. Output is seed-ordered (largest first). */
export const mergeOverlappingClusters = (
  items: ProjectedItem<ClusterData>[],
  overlapFactor: number = DEFAULT_OVERLAP_FACTOR,
): ClusterData[] => groupOverlapping(items, overlapFactor).map(mergeClusterGroup);
